import { motion } from 'framer-motion';
import { BadgeCheck, Clock3, FileDown, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { Backdrop, Logo } from '@/components/layout/Logo';
import { TiltCard } from '@/components/motion';

const FEATURES = [
  { icon: Clock3, title: 'Results in minutes', text: 'We search the UAE ICP File Validity portal for you.' },
  { icon: BadgeCheck, title: 'Live status tracking', text: 'Watch each check move from queued to done.' },
  { icon: FileDown, title: 'Instant PDF report', text: 'When your visa is ACTIVE, download a bilingual report.' },
];

/** A decorative passport-style card that animates its status stamp. */
function PassportCard() {
  return (
    <TiltCard className="relative mx-auto w-full max-w-sm">
      <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6 text-white shadow-2xl">
        <div className="absolute -right-16 -top-16 size-48 rounded-full bg-amber-300/20 blur-2xl" />
        <div className="absolute -bottom-20 -left-10 size-56 rounded-full bg-slate-500/25 blur-3xl" />
        <div className="relative flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">File validity</span>
          <ShieldCheck className="size-5 text-amber-300" />
        </div>
        <div className="relative mt-6 space-y-3 font-mono text-sm">
          {[
            ['FILE NO', '206/2026/87/1•••••'],
            ['UNIFIED NO', '•••••6789'],
            ['LAST ENTRY', '01/08/2026'],
          ].map(([k, v], i) => (
            <motion.div
              key={k}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.12 }}
              className="flex justify-between border-b border-white/10 pb-2"
            >
              <span className="text-white/50">{k}</span>
              <span>{v}</span>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ scale: 2.2, opacity: 0, rotate: -30 }}
          animate={{ scale: 1, opacity: 1, rotate: -12 }}
          transition={{ delay: 1, type: 'spring', stiffness: 220, damping: 14 }}
          className="relative mt-6 inline-flex rounded-lg border-2 border-emerald-300 px-4 py-1.5 text-lg font-black tracking-widest text-emerald-300 shadow-[0_0_30px_rgba(110,231,183,0.35)]"
        >
          ACTIVE
        </motion.div>
      </div>
    </TiltCard>
  );
}

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <Backdrop />
      <section className="relative hidden overflow-hidden bg-slate-950 p-10 text-white lg:flex lg:flex-col">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(247,201,72,0.22),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(51,65,85,0.75),transparent_55%)]" />
        <div className="grid-pattern absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
        <div className="relative">
          <Logo className="[&>span:last-child]:text-white" />
        </div>
        <div className="relative flex flex-1 flex-col justify-center gap-10 py-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h2 className="max-w-md text-4xl font-extrabold leading-tight tracking-tight">
              Know your UAE visa status{' '}
              <span className="bg-gradient-to-r from-amber-200 to-amber-400 bg-clip-text text-transparent">in minutes.</span>
            </h2>
            <p className="mt-3 max-w-md text-white/60">Enter your passport details, and we'll check the ICP portal and keep you posted.</p>
          </motion.div>
          <PassportCard />
          <ul className="grid gap-4">
            {FEATURES.map((f, i) => (
              <motion.li
                key={f.title}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + i * 0.1 }}
                className="flex items-start gap-3"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
                  <f.icon className="size-4 text-amber-300" />
                </span>
                <span>
                  <span className="block text-sm font-semibold">{f.title}</span>
                  <span className="block text-sm text-white/55">{f.text}</span>
                </span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>
      <section className="flex items-center justify-center px-4 py-10 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 24 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>
          {children}
        </motion.div>
      </section>
    </div>
  );
}
